import React from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProvider } from './contexts/AuthContext'
import { ApiProvider } from './contexts/ApiContext'
import { ApiConfigProvider } from './contexts/ApiConfigContext'
import { ChatProvider } from './contexts/ChatContext'
import ProtectedRoute from './components/ProtectedRoute'
import SimpleProtectedRoute from './components/SimpleProtectedRoute'
import RouteWithUserId from './components/RouteWithUserId'
import ErrorBoundary from './components/ErrorBoundary'
import VoiceWidget from './components/VoiceWidget'
import { Toaster } from 'react-hot-toast'

// Layouts
import AuthLayout from './layouts/AuthLayout'
import CustomerLayout from './layouts/CustomerLayout'
import AdminLayout from './layouts/AdminLayout'
import PublicLayout from './layouts/PublicLayout'

// Public Pages
import Home from './pages/public/Home'
import Policies from './pages/public/Policies'
import About from './pages/public/About'
import Contact from './pages/public/Contact'

// Auth Pages
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'

// Customer Pages
import CustomerDashboard from './pages/customer/Dashboard'
import CustomerClaims from './pages/customer/Claims'
import ClaimDetails from './pages/customer/ClaimDetails'
import CustomerPolicies from './pages/customer/Policies'
import CustomerHospitals from './pages/customer/Hospitals'
import CustomerProfile from './pages/customer/Profile'
import PolicyDetails from './pages/customer/PolicyDetails'
import ApiLogs from './pages/customer/ApiLogs'
import CustomerPayments from './pages/customer/Payments'
import CustomerDocuments from './pages/customer/Documents'
import CustomerNotifications from './pages/customer/Notifications'

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard'
import AdminCustomers from './pages/admin/Customers'
import AdminClaims from './pages/admin/Claims'
import AdminPolicies from './pages/admin/Policies'
import AdminHospitals from './pages/admin/Hospitals'
import AdminDiseases from './pages/admin/Diseases'
import AdminDocuments from './pages/admin/Documents'
import AdminPayments from './pages/admin/Payments'
import AdminPolicyTypes from './pages/admin/PolicyTypes'
import AdminCoverageRules from './pages/admin/CoverageRules'
import AdminClaimDecisions from './pages/admin/ClaimDecisions'
import AdminSessions from './pages/admin/Sessions'
import AdminUsers from './pages/admin/Users'
import AdminProfile from './pages/admin/Profile'
import AdminApiSettings from './pages/admin/ApiSettings'
import AdminNotifications from './pages/admin/Notifications'
import AdminApiLogs from './pages/admin/ApiLogs'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ApiConfigProvider>
          <ApiProvider>
            <AuthProvider>
              <ChatProvider>
                <ErrorBoundary>
                  <Router>
                <div className="App">
                  <Toaster 
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#363636',
                        color: '#fff',
                      },
                    }}
                  />
                  <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<PublicLayout />}>
                    <Route index element={<Home />} />
                    <Route path="policies" element={<Policies />} />
                    <Route path="about" element={<About />} />
                    <Route path="contact" element={<Contact />} />
                  </Route>

                  {/* Auth Routes */}
                  <Route path="/auth" element={<AuthLayout />}>
                    <Route path="login" element={<Login />} />
                    <Route path="register" element={<Register />} />
                  </Route>

                  {/* Customer Routes */}
                  <Route path="/customer" element={
                    <SimpleProtectedRoute allowedRoles={['customer']}>
                      <RouteWithUserId requiredUserId={true}>
                        <CustomerLayout />
                      </RouteWithUserId>
                    </SimpleProtectedRoute>
                  }>
                    <Route index element={<CustomerDashboard />} />
                    <Route path="claims" element={<CustomerClaims />} />
                    <Route path="claims/:claimId" element={<ClaimDetails />} />
                    <Route path="policies" element={<CustomerPolicies />} />
                    <Route path="policy-details" element={<PolicyDetails />} />
                    <Route path="hospitals" element={<CustomerHospitals />} />
                    <Route path="payments" element={<CustomerPayments />} />
                    <Route path="documents" element={<CustomerDocuments />} />
                    <Route path="notifications" element={<CustomerNotifications />} />
                    <Route path="profile" element={<CustomerProfile />} />
                    <Route path="api-logs" element={<ApiLogs />} />
                  </Route>

                  {/* Admin Routes */}
                  <Route path="/admin" element={
                    <SimpleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN', 'MANAGER', 'EMPLOYEE', 'employee', 'customer']}>
                      <RouteWithUserId requiredUserId={true}>
                        <AdminLayout />
                      </RouteWithUserId>
                    </SimpleProtectedRoute>
                  }>
                    <Route index element={<AdminDashboard />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="claims" element={<AdminClaims />} />
                    <Route path="policies" element={<AdminPolicies />} />
                    <Route path="hospitals" element={<AdminHospitals />} />
                    <Route path="diseases" element={<AdminDiseases />} />
                    <Route path="documents" element={<AdminDocuments />} />
                    <Route path="payments" element={<AdminPayments />} />
                    <Route path="policy-types" element={<AdminPolicyTypes />} />
                    <Route path="coverage-rules" element={<AdminCoverageRules />} />
                    <Route path="claim-decisions" element={<AdminClaimDecisions />} />
      <Route path="sessions" element={<AdminSessions />} />
      <Route path="users" element={<AdminUsers />} />
      <Route path="notifications" element={<AdminNotifications />} />
      <Route path="api-logs" element={<AdminApiLogs />} />
      <Route path="profile" element={<AdminProfile />} />
      <Route path="api-settings" element={<AdminApiSettings />} />
                  </Route>

                  {/* Default redirect */}
                  <Route path="*" element={<Home />} />
                  </Routes>
                  <VoiceWidget />
                </div>
                  </Router>
                </ErrorBoundary>
              </ChatProvider>
            </AuthProvider>
          </ApiProvider>
        </ApiConfigProvider>
      </QueryClientProvider>
    </HelmetProvider>
  )
}

export default App